import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api, apiWrite } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Sustained-load soak console.
 *
 * A soak is not a request-count benchmark. The API keeps a bounded pool busy for a wall-clock
 * duration, samples throughput, nearest-rank latency and its own heap once per second, then
 * settles the run against three budgets: duration, errors and memory growth. This page starts
 * runs, polls the report endpoint while the pool is active, and renders the stored evidence.
 *
 * Every number is read from the API. The browser does not recompute percentiles or slopes, so
 * two operators looking at the same run see the same verdict.
 *
 * The page is not routed from App.tsx in this part, and the API mount is intentionally unmounted
 * too. Both are one-line wiring changes reviewed with the first operator who needs the console.
 */

type Verdict = 'pass' | 'fail' | 'inconclusive'
type RunStatus = 'running' | 'passed' | 'failed' | 'error'

interface SoakRunRow {
  id: string
  target_base: string
  path: string
  method: string
  concurrency: number
  duration_seconds: number
  status: RunStatus
  verdict: Verdict
  started_at: string
  finished_at?: string
  duration_ms: number
  request_count: number
  success_count: number
  error_count: number
  error_rate: number
  rps: number
  p50_ms: number
  p95_ms: number
  p99_ms: number
  min_ms: number
  max_ms: number
  mean_ms: number
  threshold_p95_ms: number
  threshold_error_rate: number
  threshold_memory_bytes_per_min: number
  error_budget_errors: number
  error_budget_remaining: number
  error_budget_used: number
  error_budget_exceeded: boolean
  heap_start_bytes: number
  heap_end_bytes: number
  heap_peak_bytes: number
  heap_delta_bytes: number
  memory_slope_bytes_per_min: number
  memory_slope_r2: number
  memory_points: number
  memory_span_ms: number
  duration_budget_met: boolean
  duration_shortfall_ms: number
  sample_count: number
  failure_reasons: string[]
  created_by?: string
}

interface SoakSample {
  sequence: number
  elapsed_ms: number
  requests: number
  errors: number
  p50_ms: number
  p95_ms: number
  p99_ms: number
  rps: number
  heap_alloc_bytes: number
  heap_inuse_bytes: number
  goroutines: number
}

interface SoakReport {
  run: SoakRunRow
  samples: SoakSample[]
}

const PATHS: { value: string; label: string }[] = [
  { value: '/healthz', label: '/healthz · ตรวจสุขภาพพื้นฐาน' },
  { value: '/readyz', label: '/readyz · ตรวจความพร้อมของบริการ' },
  { value: '/version', label: '/version · เวอร์ชันที่กำลังให้บริการ' },
]

const VERDICT_LABEL: Record<Verdict, string> = {
  pass: 'ผ่านเกณฑ์',
  fail: 'ไม่ผ่านเกณฑ์',
  inconclusive: 'ยังไม่มีข้อสรุป',
}

const VERDICT_TONE: Record<Verdict, string> = {
  pass: 'sev-low',
  fail: 'sev-critical',
  inconclusive: 'sev-medium',
}

const STATUS_LABEL: Record<RunStatus, string> = {
  running: 'กำลังทดสอบ',
  passed: 'เสร็จสิ้น · ผ่าน',
  failed: 'เสร็จสิ้น · ไม่ผ่าน',
  error: 'ผิดพลาด',
}

const FAILURE_LABEL: Record<string, string> = {
  no_samples: 'ไม่มีคำขอที่วัดผลได้',
  error_budget_exceeded: 'ใช้เกินงบข้อผิดพลาด',
  p95_exceeded: 'p95 ช้ากว่าเกณฑ์',
  duration_budget_short: 'ความยาวการทดสอบสั้นกว่าเป้าหมาย',
  memory_growth: 'หน่วยความจำโตต่อเนื่อง',
  request_cap_reached: 'ถึงเพดานจำนวนคำขอของการทดสอบ',
  run_failed: 'การทดสอบหยุดก่อนเสร็จ',
}

function msText(value: number): string {
  if (!Number.isFinite(value)) return '—'
  if (value >= 1000) return `${(value / 1000).toFixed(2)} วินาที`
  return `${value.toFixed(2)} ms`
}

function rateText(value: number): string {
  return `${(value * 100).toFixed(2)}%`
}

function bytesText(value: number): string {
  if (!Number.isFinite(value)) return '—'
  const sign = value < 0 ? '-' : ''
  const abs = Math.abs(value)
  if (abs >= 1024 * 1024) return `${sign}${(abs / (1024 * 1024)).toFixed(2)} MiB`
  if (abs >= 1024) return `${sign}${(abs / 1024).toFixed(1)} KiB`
  return `${sign}${abs.toFixed(0)} ไบต์`
}

function slopeText(value: number): string {
  if (!Number.isFinite(value)) return '—'
  return `${(value / (1024 * 1024)).toFixed(2)} MiB/นาที`
}

function durationText(seconds: number): string {
  if (seconds >= 60 && seconds % 60 === 0) return `${seconds / 60} นาที`
  return `${seconds} วินาที`
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function intValue(raw: string, fallback: number): number {
  const parsed = Number.parseInt(raw, 10)
  return Number.isFinite(parsed) ? parsed : fallback
}

export function Soak() {
  const qc = useQueryClient()
  const [duration, setDuration] = useState('30')
  const [concurrency, setConcurrency] = useState('32')
  const [path, setPath] = useState('/healthz')
  const [thresholdP95, setThresholdP95] = useState('750')
  const [thresholdRatePct, setThresholdRatePct] = useState('1')
  const [thresholdMemory, setThresholdMemory] = useState('12')
  const [selected, setSelected] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const runsQ = useQuery({
    queryKey: ['soak-runs'],
    queryFn: () => api<SoakRunRow[]>('/v1/soak/runs?limit=50'),
    refetchInterval: 5000,
    staleTime: 2000,
  })

  const rows = runsQ.data ?? []
  const activeID = selected ?? rows[0]?.id ?? null

  const reportQ = useQuery({
    queryKey: ['soak-run', activeID],
    queryFn: () => api<SoakReport>(`/v1/soak/runs/${encodeURIComponent(activeID ?? '')}`),
    enabled: Boolean(activeID),
    refetchInterval: (query) => {
      const status = query.state.data?.run.status
      return status === 'running' ? 1500 : false
    },
  })

  const start = useMutation({
    mutationFn: () =>
      apiWrite<{ run: SoakRunRow; accepted: boolean }>('/v1/soak/runs', 'POST', {
        duration_seconds: intValue(duration, 0),
        concurrency: intValue(concurrency, 0),
        path,
        threshold_p95_ms: intValue(thresholdP95, 0),
        threshold_error_rate: Math.max(0, Math.min(1, Number(thresholdRatePct) / 100)),
        threshold_memory_mb_per_min: Math.max(0, Number(thresholdMemory)),
      }),
    onSuccess: (data) => {
      setActionError(null)
      setSelected(data.run.id)
      setNotice(
        `รับการทดสอบแล้ว · ${durationText(data.run.duration_seconds)} · คนงาน ${number(data.run.concurrency)} · ` +
        `เป้าหมาย ${data.run.path} · ระบบจะบันทึกผลเมื่อครบเวลา`,
      )
      void qc.invalidateQueries({ queryKey: ['soak-runs'] })
      void qc.invalidateQueries({ queryKey: ['soak-run', data.run.id] })
    },
    onError: (err) => setActionError(errorText(err, 'เริ่มการทดสอบไม่สำเร็จ')),
  })

  const report = reportQ.data
  const latest = report?.run ?? rows[0]
  const samples = report?.samples ?? []
  const busy = start.isPending
  const running = latest?.status === 'running'
  const canStart =
    !busy &&
    intValue(duration, 0) >= 5 &&
    intValue(duration, 0) <= 600 &&
    intValue(concurrency, 0) >= 1 &&
    intValue(concurrency, 0) <= 64

  const budgetPct = latest ? Math.min(100, Math.max(0, latest.error_budget_used * 100)) : 0
  const budgetTone = latest?.error_budget_exceeded
    ? 'var(--color-crimson)'
    : budgetPct >= 80
      ? 'var(--color-amber)'
      : 'var(--color-emerald)'

  return (
    <>
      <PageHead
        title="ทดสอบโหลดต่อเนื่อง"
        sub="ยิงคำขอ GET จริงต่อเนื่องตามระยะเวลาที่กำหนด ไปยังเส้นทางอ่านอย่างเดียวบน 127.0.0.1:8787 · วัด p50/p95/p99 · งบข้อผิดพลาด · การเติบโตของหน่วยความจำ · ตัดสินผ่านหรือไม่ผ่านจากตัวเลขที่บันทึกไว้"
        actions={
          <span className="dim" style={{ fontSize: 11.5 }}>
            {latest ? `ทดสอบล่าสุด ${thaiDateTime(latest.started_at)}` : 'ยังไม่มีการทดสอบ'}
          </span>
        }
      />

      {notice ? (
        <div className="card card-pad" role="status" style={{ marginBottom: 14, borderColor: 'var(--color-hairline-strong)' }}>
          <span className="dim" style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}

      {actionError ? (
        <div className="card card-pad" role="alert" style={{ marginBottom: 14, borderColor: 'var(--color-crimson)' }}>
          <span style={{ fontSize: 12.5, color: 'var(--color-crimson)' }}>{actionError}</span>
        </div>
      ) : null}

      <div className="grid-4 stagger">
        <Stat label="p50" value={latest ? msText(latest.p50_ms) : '—'} foot="ค่ามัธยฐานของทุกคำขอในรอบ" />
        <Stat label="p95" value={latest ? msText(latest.p95_ms) : '—'} foot={latest ? `เกณฑ์ ${msText(latest.threshold_p95_ms)}` : 'ยังไม่มีข้อมูล'} />
        <Stat label="p99" value={latest ? msText(latest.p99_ms) : '—'} foot="หางการกระจาย ไม่ใช่ค่าเฉลี่ย" />
        <Stat
          label="อัตราข้อผิดพลาด"
          value={latest ? rateText(latest.error_rate) : '—'}
          foot={latest ? `${number(latest.error_count)} จาก ${number(latest.request_count)} คำขอ` : 'ยังไม่มีข้อมูล'}
        />
      </div>

      <div className="grid-2" style={{ marginTop: 18, alignItems: 'start' }}>
        <div className="card card-pad">
          <div className="card-label">ตั้งค่าการทดสอบ</div>
          <div className="field" style={{ marginTop: 14 }}>
            <label htmlFor="soak-path">เส้นทางเป้าหมาย</label>
            <select
              id="soak-path"
              className="select"
              style={{ width: '100%' }}
              value={path}
              disabled={busy}
              onChange={(event) => setPath(event.target.value)}
            >
              {PATHS.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </div>

          <div className="grid-2">
            <div className="field">
              <label htmlFor="soak-duration">ระยะเวลา (5–600 วินาที)</label>
              <input
                id="soak-duration"
                className="input"
                type="number"
                min={5}
                max={600}
                step={5}
                value={duration}
                disabled={busy}
                onChange={(event) => setDuration(event.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="soak-concurrency">จำนวนคนงาน (1–64)</label>
              <input
                id="soak-concurrency"
                className="input"
                type="number"
                min={1}
                max={64}
                value={concurrency}
                disabled={busy}
                onChange={(event) => setConcurrency(event.target.value)}
              />
            </div>
          </div>

          <div className="grid-2">
            <div className="field">
              <label htmlFor="soak-p95">เกณฑ์ p95 (ms)</label>
              <input
                id="soak-p95"
                className="input"
                type="number"
                min={1}
                max={60000}
                value={thresholdP95}
                disabled={busy}
                onChange={(event) => setThresholdP95(event.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="soak-rate">งบข้อผิดพลาด (%)</label>
              <input
                id="soak-rate"
                className="input"
                type="number"
                min={0}
                max={100}
                step={0.1}
                value={thresholdRatePct}
                disabled={busy}
                onChange={(event) => setThresholdRatePct(event.target.value)}
              />
            </div>
          </div>

          <div className="field">
            <label htmlFor="soak-memory">เพดานการโตของหน่วยความจำ (MiB/นาที)</label>
            <input
              id="soak-memory"
              className="input"
              type="number"
              min={0}
              max={1024}
              step={1}
              value={thresholdMemory}
              disabled={busy}
              onChange={(event) => setThresholdMemory(event.target.value)}
            />
          </div>

          <div className="row" style={{ marginTop: 16, gap: 10 }}>
            <button className="btn btn-primary" disabled={!canStart} onClick={() => start.mutate()}>
              {busy ? 'กำลังเริ่ม…' : 'เริ่มทดสอบต่อเนื่อง'}
            </button>
            <span className="dim" style={{ fontSize: 11.5 }}>
              คำขอเป็น GET จริง · สูงสุด 64 คนงาน · POST ต้องมี Idempotency-Key
            </span>
          </div>
          {running ? (
            <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
              กำลังทดสอบอยู่ · ระบบจะดึงผลทุก 1.5 วินาทีจนกว่ารอบจะจบ
            </div>
          ) : null}
        </div>

        <div className="card card-pad">
          <div className="card-label">สรุปผลที่เลือก</div>
          {reportQ.isLoading && activeID ? (
            <div style={{ marginTop: 12 }}><Skel h={160} /></div>
          ) : latest ? (
            <>
              <div className="row" style={{ marginTop: 12, gap: 8 }}>
                <span className="mono" style={{ fontSize: 12 }}>{latest.path}</span>
                <span className={`sev ${VERDICT_TONE[latest.verdict]}`}>
                  <i className="sev-dot" />
                  {STATUS_LABEL[latest.status]}
                </span>
              </div>
              <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>
                {thaiDateTime(latest.started_at)} · {durationText(latest.duration_seconds)} · คนงาน {latest.concurrency} ·
                {' '}{number(latest.request_count)} คำขอ · {latest.rps.toFixed(2)} RPS
              </div>

              <div className="grid-2" style={{ marginTop: 16 }}>
                <div>
                  <div className="card-label">เวลาแฝง</div>
                  <div className="stack" style={{ marginTop: 8 }}>
                    {[
                      ['p50', latest.p50_ms],
                      ['p95', latest.p95_ms],
                      ['p99', latest.p99_ms],
                      ['ต่ำสุด / สูงสุด', null],
                    ].map(([label, value]) => (
                      <div key={String(label)} className="row" style={{ justifyContent: 'space-between' }}>
                        <span className="dim" style={{ fontSize: 12 }}>{label}</span>
                        <span className="tnum" style={{ fontSize: 12.5 }}>
                          {value === null ? `${msText(latest.min_ms)} / ${msText(latest.max_ms)}` : msText(Number(value))}
                        </span>
                      </div>
                    ))}
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span className="dim" style={{ fontSize: 12 }}>เวลารวม</span>
                      <span className="tnum" style={{ fontSize: 12.5 }}>{msText(latest.duration_ms)}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="card-label">งบข้อผิดพลาด</div>
                  <div className="stack" style={{ marginTop: 8 }}>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span className="dim" style={{ fontSize: 12 }}>อนุญาต</span>
                      <span className="tnum" style={{ fontSize: 12.5 }}>{number(latest.error_budget_errors)} คำขอ</span>
                    </div>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span className="dim" style={{ fontSize: 12 }}>ใช้ไป</span>
                      <span className="tnum" style={{ fontSize: 12.5 }}>{number(latest.error_count)} คำขอ</span>
                    </div>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span className="dim" style={{ fontSize: 12 }}>เหลือ</span>
                      <span className="tnum" style={{ fontSize: 12.5 }}>{number(latest.error_budget_remaining)} คำขอ</span>
                    </div>
                    <div className="row" style={{ gap: 10, marginTop: 2 }}>
                      <span className="bar"><i style={{ width: `${budgetPct}%`, background: budgetTone }} /></span>
                      <span className="tnum" style={{ fontSize: 11.5 }}>{budgetPct.toFixed(1)}%</span>
                    </div>
                    <div className="dim" style={{ fontSize: 11 }}>
                      อัตราที่อนุญาต {rateText(latest.threshold_error_rate)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid-2" style={{ marginTop: 16 }}>
                <div>
                  <div className="card-label">หน่วยความจำ</div>
                  <div className="stack" style={{ marginTop: 8 }}>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span className="dim" style={{ fontSize: 12 }}>เริ่ม / สิ้นสุด</span>
                      <span className="tnum" style={{ fontSize: 12.5 }}>
                        {bytesText(latest.heap_start_bytes)} / {bytesText(latest.heap_end_bytes)}
                      </span>
                    </div>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span className="dim" style={{ fontSize: 12 }}>ผลต่าง</span>
                      <span className="tnum" style={{ fontSize: 12.5 }}>{bytesText(latest.heap_delta_bytes)}</span>
                    </div>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span className="dim" style={{ fontSize: 12 }}>ความชัน</span>
                      <span className="tnum" style={{ fontSize: 12.5 }}>{slopeText(latest.memory_slope_bytes_per_min)}</span>
                    </div>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span className="dim" style={{ fontSize: 12 }}>R² · จุดตัวอย่าง</span>
                      <span className="tnum" style={{ fontSize: 12.5 }}>{latest.memory_slope_r2.toFixed(3)} · {number(latest.memory_points)}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="card-label">การตัดสิน</div>
                  <div className="stack" style={{ marginTop: 8 }}>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span className="dim" style={{ fontSize: 12 }}>งบระยะเวลา</span>
                      <span className="tnum" style={{ fontSize: 12.5 }}>
                        {latest.duration_budget_met ? 'ครบ' : `ขาด ${msText(latest.duration_shortfall_ms)}`}
                      </span>
                    </div>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span className="dim" style={{ fontSize: 12 }}>หลักฐานรายวินาที</span>
                      <span className="tnum" style={{ fontSize: 12.5 }}>{number(latest.sample_count)} แถว</span>
                    </div>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span className="dim" style={{ fontSize: 12 }}>เพดานหน่วยความจำ</span>
                      <span className="tnum" style={{ fontSize: 12.5 }}>{slopeText(latest.threshold_memory_bytes_per_min)}</span>
                    </div>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span className="dim" style={{ fontSize: 12 }}>ผล</span>
                      <span className={`sev ${VERDICT_TONE[latest.verdict]}`}>
                        <i className="sev-dot" />
                        {VERDICT_LABEL[latest.verdict]}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {latest.failure_reasons.length > 0 ? (
                <div className="row" style={{ gap: 6, marginTop: 14, flexWrap: 'wrap' }}>
                  {latest.failure_reasons.map((code) => (
                    <span key={code} className="chip">{FAILURE_LABEL[code] ?? code}</span>
                  ))}
                </div>
              ) : null}
            </>
          ) : (
            <div style={{ marginTop: 12 }}>
              <Empty
                title="ยังไม่ได้เลือกผลการทดสอบ"
                body="เริ่มการทดสอบต่อเนื่อง หรือเลือกแถวจากตารางด้านล่างเพื่อดูงบข้อผิดพลาดและแนวโน้มหน่วยความจำ"
              />
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 18 }}>
        <div className="row" style={{ padding: '16px 20px', gap: 12 }}>
          <span className="card-label">ประวัติการทดสอบ</span>
          <button
            className="btn btn-ghost btn-sm"
            style={{ marginLeft: 'auto' }}
            onClick={() => void runsQ.refetch()}
            disabled={runsQ.isFetching}
          >
            {runsQ.isFetching ? 'กำลังโหลด…' : 'รีเฟรช'}
          </button>
        </div>

        {runsQ.isLoading ? (
          <div style={{ padding: 20 }} className="stack"><Skel h={44} /><Skel h={44} /><Skel h={44} /></div>
        ) : runsQ.isError ? (
          <div style={{ padding: 20 }}>
            <Empty
              title="โหลดประวัติการทดสอบไม่สำเร็จ"
              body={errorText(runsQ.error, 'เรียก /v1/soak/runs ไม่สำเร็จ — ตรวจสอบว่าเส้นทางถูก mount แล้ว')}
            />
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 20 }}>
            <Empty
              title="ยังไม่มีการทดสอบที่บันทึกไว้"
              body="เมื่อเริ่มการทดสอบ ระบบจะบันทึกหัวรายการลง platform.soak_runs และหลักฐานรายวินาทีลง platform.soak_samples"
            />
          </div>
        ) : (
          <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
            <table className="data">
              <thead>
                <tr>
                  <th>เป้าหมาย</th>
                  <th className="num">ระยะเวลา</th>
                  <th className="num">คำขอ</th>
                  <th className="num">p50</th>
                  <th className="num">p95</th>
                  <th className="num">p99</th>
                  <th className="num">ผิดพลาด</th>
                  <th className="num">หน่วยความจำ</th>
                  <th>ผล</th>
                  <th>เริ่ม</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => setSelected(row.id)}
                    style={row.id === activeID ? { background: 'rgba(200, 169, 106, 0.06)' } : undefined}
                  >
                    <td>
                      <div className="mono" style={{ fontSize: 12 }}>{row.path}</div>
                      <div className="dim" style={{ fontSize: 11 }}>
                        คนงาน {row.concurrency} · {durationText(row.duration_seconds)}
                      </div>
                    </td>
                    <td className="num">{durationText(row.duration_seconds)}</td>
                    <td className="num">{number(row.request_count)}</td>
                    <td className="num">{msText(row.p50_ms)}</td>
                    <td className="num">{msText(row.p95_ms)}</td>
                    <td className="num">{msText(row.p99_ms)}</td>
                    <td className="num">{number(row.error_count)}</td>
                    <td className="num">{slopeText(row.memory_slope_bytes_per_min)}</td>
                    <td>
                      <span className={`sev ${VERDICT_TONE[row.verdict]}`}>
                        <i className="sev-dot" />
                        {STATUS_LABEL[row.status]}
                      </span>
                    </td>
                    <td className="dim" style={{ fontSize: 11.5 }}>{thaiDateTime(row.started_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {samples.length > 0 ? (
        <div className="card" style={{ marginTop: 18 }}>
          <div className="row" style={{ padding: '16px 20px', gap: 12 }}>
            <span className="card-label">หลักฐานรายวินาที</span>
            <span className="dim" style={{ fontSize: 11.5, marginLeft: 'auto' }}>
              {number(samples.length)} แถว · หนึ่งแถวต่อหนึ่งวินาที
            </span>
          </div>
          <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
            <table className="data">
              <thead>
                <tr>
                  <th className="num">วินาทีที่</th>
                  <th className="num">คำขอ</th>
                  <th className="num">ผิดพลาด</th>
                  <th className="num">p50</th>
                  <th className="num">p95</th>
                  <th className="num">p99</th>
                  <th className="num">RPS</th>
                  <th className="num">Heap</th>
                  <th className="num">Goroutine</th>
                </tr>
              </thead>
              <tbody>
                {samples.map((sample) => (
                  <tr key={sample.sequence}>
                    <td className="num">{sample.sequence}</td>
                    <td className="num">{number(sample.requests)}</td>
                    <td className="num">{number(sample.errors)}</td>
                    <td className="num">{msText(sample.p50_ms)}</td>
                    <td className="num">{msText(sample.p95_ms)}</td>
                    <td className="num">{msText(sample.p99_ms)}</td>
                    <td className="num">{sample.rps.toFixed(1)}</td>
                    <td className="num">{bytesText(sample.heap_alloc_bytes)}</td>
                    <td className="num">{number(sample.goroutines)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </>
  )
}
